import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { extractStripeApiError, isExistingStripeAccountError } from '../lib/stripeConnect'

const POLL_DELAYS_MS = [500, 700, 1000, 2000, 3000, 5000, 5000, 8000, 8000, 8000]
const MAX_POLL_ATTEMPTS = POLL_DELAYS_MS.length

export default function useStripeConnect({
  entityId,
  getAccountStatus,
  createStripeAccount,
  getOnboardingLink,
  returnUrl,
  refreshUrl,
  isComplete,
  onVerificationComplete,
  enabled = true,
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [stripeStatus, setStripeStatus] = useState(null)
  const [loadingStatus, setLoadingStatus] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [polling, setPolling] = useState(false)
  const [error, setError] = useState(null)
  const [stripeMessage, setStripeMessage] = useState(null)
  const pollAttemptRef = useRef(0)
  const stripeReturnHandledRef = useRef(false)

  const fetchStatusSilent = useCallback(async () => {
    if (!entityId || !enabled) return null

    try {
      const response = await getAccountStatus(entityId)
      if (response?.data) {
        setStripeStatus(response.data)
        return response.data
      }
    } catch (err) {
      // Ignore transient errors while polling after Stripe return.
      console.error('Stripe status poll failed:', err)
    }

    return null
  }, [entityId, enabled, getAccountStatus])

  const fetchStatus = useCallback(async () => {
    if (!entityId || !enabled) return null

    setLoadingStatus(true)
    setError(null)

    try {
      const response = await getAccountStatus(entityId)
      if (response?.data) {
        setStripeStatus(response.data)
        return response.data
      }
      if (response?.error) {
        setError(
          extractStripeApiError(response.error, 'Failed to fetch status'),
        )
      }
    } catch (err) {
      setError(err?.message || 'Failed to fetch Stripe status')
    } finally {
      setLoadingStatus(false)
    }

    return null
  }, [entityId, enabled, getAccountStatus])

  useEffect(() => {
    if (!entityId || !enabled) return

    let cancelled = false

    const run = async () => {
      setLoadingStatus(true)
      setError(null)
      try {
        const response = await getAccountStatus(entityId)
        if (cancelled) return
        if (response?.data) {
          setStripeStatus(response.data)
        } else if (response?.error) {
          setError(
            extractStripeApiError(response.error, 'Failed to fetch status'),
          )
        }
      } catch (err) {
        if (cancelled) return
        setError(err?.message || 'Failed to fetch Stripe status')
      } finally {
        if (!cancelled) {
          setLoadingStatus(false)
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [entityId, enabled, getAccountStatus])

  const clearStripeQueryParams = useCallback(() => {
    if (
      !searchParams.has('stripe_return') &&
      !searchParams.has('stripe_refresh')
    ) {
      return
    }
    searchParams.delete('stripe_return')
    searchParams.delete('stripe_refresh')
    setSearchParams(searchParams, { replace: true })
  }, [searchParams, setSearchParams])

  const pollUntilComplete = useCallback(async () => {
    pollAttemptRef.current = 0
    setPolling(true)

    const poll = async () => {
      pollAttemptRef.current += 1
      const status = await fetchStatusSilent()

      if (status && isComplete?.(status)) {
        setPolling(false)
        setStripeMessage({ type: 'success', key: 'returnSuccess' })
        onVerificationComplete?.(status)
        return
      }

      if (pollAttemptRef.current >= MAX_POLL_ATTEMPTS) {
        setPolling(false)
        return
      }

      const delay =
        POLL_DELAYS_MS[pollAttemptRef.current] ??
        POLL_DELAYS_MS[POLL_DELAYS_MS.length - 1]
      window.setTimeout(poll, delay)
    }

    await poll()
  }, [fetchStatusSilent, isComplete, onVerificationComplete])

  useEffect(() => {
    if (!entityId || !enabled || stripeReturnHandledRef.current) return

    const stripeReturn = searchParams.get('stripe_return')
    const stripeRefresh = searchParams.get('stripe_refresh')

    if (stripeReturn === 'true') {
      stripeReturnHandledRef.current = true
      pollUntilComplete()
      clearStripeQueryParams()
    } else if (stripeRefresh === 'true') {
      stripeReturnHandledRef.current = true
      setStripeMessage({ type: 'warning', key: 'sessionExpired' })
      clearStripeQueryParams()
    }
  }, [
    clearStripeQueryParams,
    enabled,
    entityId,
    pollUntilComplete,
    searchParams,
  ])

  const handleConnect = useCallback(async () => {
    if (!entityId) return

    setConnecting(true)
    setError(null)

    try {
      const isNotCreated = !stripeStatus?.has_stripe_account

      if (isNotCreated) {
        const createResponse = await createStripeAccount(entityId)

        if (createResponse?.data?.onboarding_url) {
          window.location.href = createResponse.data.onboarding_url
          return
        }

        if (createResponse?.error) {
          const createError = extractStripeApiError(
            createResponse.error,
            'Failed to create Stripe account',
          )
          if (!isExistingStripeAccountError(createError)) {
            throw new Error(createError)
          }
        }
      }

      const linkResponse = await getOnboardingLink(
        entityId,
        returnUrl,
        refreshUrl,
      )

      if (linkResponse?.data?.onboarding_url) {
        window.location.href = linkResponse.data.onboarding_url
      } else {
        throw new Error(
          extractStripeApiError(
            linkResponse?.error,
            'Failed to get onboarding link',
          ),
        )
      }
    } catch (err) {
      setError(err?.message || 'Failed to connect Stripe')
      setConnecting(false)
    }
  }, [
    createStripeAccount,
    entityId,
    getOnboardingLink,
    refreshUrl,
    returnUrl,
    stripeStatus?.has_stripe_account,
  ])

  return {
    connecting,
    error,
    fetchStatus,
    handleConnect,
    loadingStatus,
    polling,
    setError,
    setStripeMessage,
    stripeMessage,
    stripeStatus,
  }
}
